import axios from 'axios'
import Cookies from 'js-cookie'

import { ViewItemListTracker } from 'Pods/Shared/UserTracker/UserTracker'

import configureReactRailsContext, { unwrapDefaultExport } from 'Utils/configureReactRailsContext'
import { createDeferredMounter } from 'Utils/createDeferredMounter'
import { consumePendingToast } from 'Utils/pendingToast'
import { initSentry, setSentryUser } from 'Utils/SentryConfig'
import loadLocaleBundle from 'Translations/loadLocaleBundle'

consumePendingToast()

// Start fetching the active locale's translations at script-eval time so the
// bundle is registered before components hydrate (no English flash on non-EN
// pages). Resolves immediately on English pages.
const localeReady = loadLocaleBundle()

// Initialize Sentry immediately but defer actual initialization
initSentry(window)

// Initialize tracking before React mounts
ViewItemListTracker()

window.addEventListener('load', () => {
  // Set user information if available
  const uuid = Cookies.get('_cgtrader_uuid')
  const username = Cookies.get('_cgtrader_utoken')
  setSentryUser(uuid, username)


  // Configure axios with CSRF token (same as application.jsx)
  const metaToken = document.getElementsByName('csrf-token')[0]
  let token = null

  if (metaToken) {
    token = metaToken.getAttribute('content')
    axios.defaults.headers.common['X-CSRF-Token'] = token
  }

  axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'

  localeReady.then(() => {
    const ReactRailsUJS = require('react_ujs')
    // BottomSection, FullScreenGallery and PreviewItemPage are excluded from the
    // eager context so their code ships in async chunks: BottomSection loads when
    // the bottom container is about to hydrate (see mountBottom), FullScreenGallery
    // when the gallery zoom is opened (see GalleryArea), and PreviewItemPage only
    // on the seller preview page (see mountTop). Their SSR HTML is unaffected.
    const context = require.context(
      'Pods',
      true,
      /^(?=.*\/(ItemPage|Shared\/ItemPageComponents)\/)(?!.*(?:test|story|BottomSection|FullScreenGallery|PreviewItemPage)).*$/
    )

    configureReactRailsContext(ReactRailsUJS, context)

    // Components registered from dynamically imported chunks take priority over
    // the eager context when react_ujs resolves data-react-class names.
    const dynamicComponents = {}
    const contextGetConstructor = ReactRailsUJS.getConstructor
    ReactRailsUJS.getConstructor = (componentName) => (
      dynamicComponents[componentName] || contextGetConstructor(componentName)
    )

    // The seller preview page renders PreviewItemPage into the same container;
    // its chunk is loaded on demand so the public PDP never downloads it.
    const previewNode = document.querySelector('[data-react-class="ItemPage/PreviewItemPage/PreviewItemPage"]')
    const mountTop = () => { ReactRailsUJS.mountComponents("#fast-item-page") }

    if (previewNode) {
      import(/* webpackChunkName: "item-page-preview" */ 'Pods/ItemPage/PreviewItemPage/PreviewItemPage')
        .then((module) => {
          dynamicComponents['ItemPage/PreviewItemPage/PreviewItemPage'] = unwrapDefaultExport(module)
        })
        .catch((error) => {
          window.sendErrorToSentry(error, { tags: { section: 'item-page-preview-chunk' } })
        })
        // Always mount the container, even if the preview chunk fails: otherwise a
        // chunk-load error (network/CDN/deploy race) leaves the whole seller preview
        // page blank instead of just dropping PreviewItemPage.
        .finally(mountTop)
    } else {
      mountTop()
    }

    // BottomSection always lives in its own container (public PDP and seller
    // preview alike), so its chunk and hydration stay off the critical path.
    const bottomContainer = document.getElementById('fast-item-page-bottom')

    if (bottomContainer) {
      let observer
      const { mountSection: mountBottom } = createDeferredMounter({
        loadChunk: () => import(/* webpackChunkName: "item-page-bottom" */ 'Pods/Shared/ItemPageComponents/BottomSection/BottomSection')
          .then((module) => {
            dynamicComponents['Shared/ItemPageComponents/BottomSection/BottomSection'] = unwrapDefaultExport(module)
          }),
        onMount: () => { ReactRailsUJS.mountComponents("#fast-item-page-bottom") },
        onError: (error) => {
          window.sendErrorToSentry(error, { tags: { section: 'item-page-bottom-chunk' } })
        },
      })

      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            observer.disconnect()
            mountBottom()
          }
        }, { rootMargin: '600px' })

        observer.observe(bottomContainer)
        // Fallback: hydrate when the main thread goes idle (or after the timeout) so the section
        // still becomes interactive without a scroll (deep links, no-scroll sessions) without
        // competing with critical pre-TTI work.
        if ('requestIdleCallback' in window) {
          requestIdleCallback(mountBottom, { timeout: 10000 })
        } else {
          setTimeout(mountBottom, 8000)
        }
      } else {
        mountBottom()
      }
    }
  })
})
